// ==========================================================================
// FILE: src/components/ui/index.jsx
//
// Global reusable UI primitives.
// Import anywhere:  import { AppButton, AppSelect, ... } from "@/components/ui"
// ==========================================================================

import React, { useRef, useEffect, useState } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// AppButton  — universal button with built-in spinner on loading
//
// Props:
//   variant   "primary" | "success" | "danger" | "purple" | "ghost" | "warning"
//   size      "sm" | "md" | "lg"
//   loading   false | true | "Custom label while loading…"
//   disabled  bool
//   icon      ReactNode — shown left of label (hidden while loading)
//   type      "button" | "submit" | "reset"
// ─────────────────────────────────────────────────────────────────────────────
export const AppButton = ({
  children,
  onClick,
  type = "button",
  loading = false,
  disabled = false,
  variant = "primary",
  size = "md",
  icon,
  className = "",
}) => {
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white focus-visible:ring-blue-500",
    success: "bg-emerald-600 hover:bg-emerald-700 text-white focus-visible:ring-emerald-500",
    danger: "bg-red-600 hover:bg-red-700 text-white focus-visible:ring-red-500",
    purple: "bg-purple-600 hover:bg-purple-700 text-white focus-visible:ring-purple-500",
    warning: "bg-yellow-500 hover:bg-yellow-600 text-white focus-visible:ring-yellow-400",
    ghost: "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 focus-visible:ring-gray-400",
  };

  const sizes = {
    sm: "px-3 py-1.5 text-xs gap-1.5",
    md: "px-4 py-2 text-sm gap-2",
    lg: "px-5 py-2.5 text-base gap-2",
  };

  const isLoading = Boolean(loading);
  const loadingLabel = typeof loading === "string" ? loading : null;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`
        inline-flex items-center justify-center rounded-lg font-medium
        transition-all duration-150 focus:outline-none focus-visible:ring-2
        disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap
        ${variants[variant] ?? variants.primary}
        ${sizes[size] ?? sizes.md}
        ${className}
      `}
    >
      {isLoading ? (
        /* spinner */
        <svg
          className="animate-spin h-4 w-4 shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        icon && <span className="shrink-0 leading-none">{icon}</span>
      )}
      <span>{loadingLabel ?? children}</span>
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AppInput  — labeled text / number input
// ─────────────────────────────────────────────────────────────────────────────
export const AppInput = ({
  label,
  id,
  type = "text",
  placeholder,
  value,
  onChange,
  required,
  min,
  className = "",
  hint,
}) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    {label && (
      <label htmlFor={id} className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    <input
      id={id}
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      min={min}
      className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900
        placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500
        focus:border-transparent transition-shadow"
    />
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// AppSelect  — labeled native <select>
// ─────────────────────────────────────────────────────────────────────────────
export const AppSelect = ({
  label,
  id,
  value,
  onChange,
  options = [],
  placeholder,
  className = "",
  disabled,
}) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    {label && (
      <label htmlFor={id} className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </label>
    )}
    <select
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900
        bg-white focus:outline-none focus:ring-2 focus:ring-blue-500
        focus:border-transparent transition-shadow disabled:opacity-50 disabled:bg-gray-50"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// AppCheckbox  — single checkbox row with indeterminate + indent support
// ─────────────────────────────────────────────────────────────────────────────
export const AppCheckbox = ({
  id,
  label,
  checked,
  onChange,
  indeterminate = false,
  indent = 0,
  badge,
  className = "",
}) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      htmlFor={id}
      className={`flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer
        hover:bg-gray-50 transition-colors ${className}`}
      style={{ paddingLeft: indent * 16 + 8 }}
    >
      <input
        ref={ref}
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded border-gray-300 text-blue-600
          focus:ring-blue-500 focus:ring-offset-0 cursor-pointer"
      />
      <span className="text-sm text-gray-700 flex-1">{label}</span>
      {badge !== undefined && (
        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{badge}</span>
      )}
    </label>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TabBar + AppTab  — horizontal tab navigation
// ─────────────────────────────────────────────────────────────────────────────
export const AppTab = ({ label, icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg
      transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
      ${active
        ? "bg-blue-600 text-white shadow-sm"
        : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-gray-900"
      }
    `}
  >
    {icon && <span className="text-base leading-none">{icon}</span>}
    {label}
  </button>
);

export const TabBar = ({ tabs, active, onChange }) => (
  <div className="flex flex-wrap gap-2 mb-6">
    {tabs.map((tab) => (
      <AppTab
        key={tab.id}
        label={tab.label}
        icon={tab.icon}
        active={active === tab.id}
        onClick={() => onChange(tab.id)}
      />
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// FilterChip  — category / subcategory pill toggle
// ─────────────────────────────────────────────────────────────────────────────
export const FilterChip = ({ label, active, color = "blue", onClick }) => {
  const colors = {
    blue: active
      ? "bg-blue-600 text-white border-blue-600"
      : "bg-white text-gray-700 border-gray-200 hover:border-blue-400 hover:text-blue-600",
    green: active
      ? "bg-emerald-600 text-white border-emerald-600"
      : "bg-white text-gray-700 border-gray-200 hover:border-emerald-400 hover:text-emerald-600",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-all duration-150
        focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
        ${colors[color] ?? colors.blue}`}
    >
      {label}
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SectionCard  — white card wrapper with optional header
// ─────────────────────────────────────────────────────────────────────────────
export const SectionCard = ({ title, icon, children, className = "" }) => (
  <div className={`bg-white rounded-xl border border-gray-100 shadow-sm ${className}`}>
    {(title || icon) && (
      <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100">
        {icon && <span className="text-lg">{icon}</span>}
        {title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
      </div>
    )}
    <div className="px-6 py-5">{children}</div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// StatusBanner  — success / info / warning / error inline alert
// ─────────────────────────────────────────────────────────────────────────────
export const StatusBanner = ({ type = "info", children }) => {
  const styles = {
    success: "bg-emerald-50 border-emerald-200 text-emerald-800",
    info: "bg-blue-50 border-blue-200 text-blue-800",
    warning: "bg-yellow-50 border-yellow-200 text-yellow-800",
    error: "bg-red-50 border-red-200 text-red-800",
  };
  const icons = { success: "✅", info: "ℹ️", warning: "⚠️", error: "❌" };

  return (
    <div className={`flex items-start gap-2 border rounded-lg px-4 py-3 text-sm ${styles[type] ?? styles.info}`}>
      <span className="mt-0.5 shrink-0">{icons[type]}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ProgressBar  — labelled progress bar + optional extra stats
// ─────────────────────────────────────────────────────────────────────────────
export const ProgressBar = ({ label, value, total, extra = [] }) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm text-gray-600">
        <span>{label}</span>
        <span className="font-medium">{value} / {total} ({pct}%)</span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div
          className="bg-blue-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      {extra.map((e, i) => (
        <p key={i} className="text-xs text-gray-500">
          {e.label}: <span className="font-medium text-gray-700">{e.value}</span>
        </p>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// DataTable  — generic column/row table
//
// columns: [{ key, label, render?: (cellValue, row) => ReactNode }]
// rows:    objects; add __key property for React key (fallback to index)
// ─────────────────────────────────────────────────────────────────────────────
export const DataTable = ({ columns = [], rows = [], emptyText = "No data found" }) => (
  <div className="overflow-x-auto rounded-lg border border-gray-100">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-gray-50 border-b border-gray-100">
          {columns.map((col) => (
            <th key={col.key} className="px-4 py-3 text-left text-xs font-semibold
              text-gray-500 uppercase tracking-wide whitespace-nowrap">
              {col.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-400 text-sm">
              {emptyText}
            </td>
          </tr>
        ) : (
          rows.map((row, i) => (
            <tr key={row.__key ?? i} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
              {columns.map((col) => (
                <td key={col.key} className="px-4 py-3 text-gray-700">
                  {col.render ? col.render(row[col.key], row) : (row[col.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// ConfirmModal  — confirmation dialog with loading state on confirm button
//
// New props vs old:
//   confirmLoading  bool | string  — shows spinner on the confirm button itself
// ─────────────────────────────────────────────────────────────────────────────
export const ConfirmModal = ({
  isOpen,
  title = "Are you sure?",
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmLoading = false,   // ← NEW: spinner on confirm button
  onConfirm,
  onCancel,
  variant = "danger",
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={!confirmLoading ? onCancel : undefined} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 space-y-4">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {message && <p className="text-sm text-gray-600">{message}</p>}
        <div className="flex gap-3 justify-end">
          <AppButton variant="ghost" onClick={onCancel} disabled={Boolean(confirmLoading)}>
            {cancelLabel}
          </AppButton>
          <AppButton
            variant={variant}
            onClick={onConfirm}
            loading={confirmLoading}   // ← spinner fires here
            disabled={Boolean(confirmLoading)}
          >
            {confirmLabel}
          </AppButton>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CampaignDropdown  — hierarchical 3-level multi-select dropdown
// (category → subcategory → campaign, with indeterminate checkboxes)
// ─────────────────────────────────────────────────────────────────────────────
export const CampaignDropdown = ({
  groupedCampaigns = [],
  uncategorized = [],
  selected = [],
  onToggleCampaign,
  onToggleSubcategory,
  onToggleCategory,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const getCampaignValue = (campaign) => {

    return (
      typeof campaign.id === "string" &&
      campaign.id.startsWith("native_")
    )
      ? campaign.id
      : campaign.campaign_name;
  };

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between border border-gray-200 bg-white
          rounded-lg px-3 py-2 text-sm text-gray-700 hover:border-blue-400 transition-colors
          focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span>
          {selected.length > 0
            ? <><span className="font-medium text-blue-600">{selected.length}</span> campaign{selected.length !== 1 ? "s" : ""} selected</>
            : <span className="text-gray-400">Select campaigns…</span>
          }
        </span>
        <svg className={`h-4 w-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200
          rounded-xl shadow-lg z-50 max-h-72 overflow-y-auto py-1">

          {groupedCampaigns.map((cat) => {
            // const allNames = cat.subcategories.flatMap((s) => s.campaigns.map((c) => c.campaign_name));
            const allNames = cat.subcategories.flatMap((s) => s.campaigns.map(getCampaignValue));
            const allChecked = allNames.length > 0 && allNames.every((n) => selected.includes(n));
            const someChecked = allNames.some((n) => selected.includes(n));

            return (
              <div key={cat.id}>
                <AppCheckbox
                  id={`cat-${cat.id}`}
                  label={cat.name}
                  checked={allChecked}
                  indeterminate={!allChecked && someChecked}
                  onChange={() => onToggleCategory(cat)}
                  className="bg-gray-50 font-semibold"
                />
                {cat.subcategories.map((sub) => {
                  // const subNames = sub.campaigns.map((c) => c.campaign_name);
                  const subNames = sub.campaigns.map(getCampaignValue);
                  const subAll = subNames.length > 0 && subNames.every((n) => selected.includes(n));
                  const subSome = subNames.some((n) => selected.includes(n));
                  return (
                    <div key={sub.id}>
                      <AppCheckbox
                        id={`sub-${sub.id}`}
                        label={sub.name}
                        checked={subAll}
                        indeterminate={!subAll && subSome}
                        onChange={() => onToggleSubcategory(sub)}
                        badge={sub.campaigns.length}
                        indent={1}
                      />

                      {sub.campaigns.map((c) => (
                        <AppCheckbox
                          key={c.id}
                          id={`c-${c.id}`}
                          label={`${c.campaign_name} (${c.email_count || 0})`}
                          checked={selected.includes(getCampaignValue(c))}
                          onChange={() => onToggleCampaign( getCampaignValue(c)) }
                          indent={2}
                        />
                       
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {uncategorized.length > 0 && (
            <div>
              <div className="px-3 py-1.5 text-xs font-semibold text-red-500 uppercase tracking-wide bg-red-50">
                Uncategorized
              </div>
              {uncategorized.map((c) => (
                <AppCheckbox
                  key={c.id}
                  id={`uc-${c.id}`}
                  label={c.campaign_name}
                  checked={selected.includes(c.campaign_name)}
                  onChange={() => onToggleCampaign(c.campaign_name)}
                  indent={1}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FieldRow  — read-only horizontal label + value pair
// ─────────────────────────────────────────────────────────────────────────────
export const FieldRow = ({ label, value }) => (
  <div className="flex gap-3 text-sm">
    <span className="font-medium text-gray-600 w-28 shrink-0">{label}:</span>
    <span className="text-gray-900">{value ?? "—"}</span>
  </div>
);